/**
 * Run-and-analyze agent: runs an execute job (script on dataset), waits for completion,
 * fetches the result, then asks the AI to analyze the results.
 * Gives the frontend AI agent the capability to "run and analyze" from the script editor.
 * Enriches result with Summary statistics (for stats) and sample/aggregated data for the AI.
 */

import { jobsApi } from '@/services/api/jobs'
import apiClient from '@/services/api/client'
import { generateResultsExplanationPrompt } from './ai-prompts'
import type { ResultsExplanationContext } from './ai-prompts'

const SAMPLE_ROWS = 80
const SUMMARY_MAX_NUMERIC_KEYS = 24

/** Compute summary statistics over stats result rows (same logic as ScriptEditorPage Summary statistics). */
function computeSummaryStatistics(
  rows: Record<string, unknown>[],
  statsColumnTypes?: Record<string, string>
): Record<string, number | string> {
  if (!rows.length || !rows[0]) return { Rows: 0 }
  const rawStatsTypes = statsColumnTypes && Object.keys(statsColumnTypes).length > 0
  const aggType = (key: string) => (rawStatsTypes ? statsColumnTypes?.[key] : undefined)
  const useAverageForSummary = (key: string) => {
    const t = aggType(key)
    if (t === 'percent' || t === 'avg') return true
    if (!rawStatsTypes && /pct|percent|accuracy|rate/i.test(key)) return true
    return false
  }
  const formatLabel = (key: string) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const summary: Record<string, number | string> = { Rows: rows.length }
  const numericKeys = Object.keys(rows[0]).filter((k) => typeof (rows[0] as any)[k] === 'number')
  const groupKeys = Object.keys(rows[0]).filter((k) => typeof (rows[0] as any)[k] !== 'number')
  groupKeys.forEach((col) => {
    const values = [...new Set(rows.map((r) => (r as any)[col]))]
    summary[`Unique ${formatLabel(col)}`] = values.length
  })
  numericKeys.slice(0, SUMMARY_MAX_NUMERIC_KEYS).forEach((key) => {
    const nums = rows.map((r) => (r as any)[key] as number).filter((n) => typeof n === 'number')
    if (nums.length === 0) return
    summary[formatLabel(key)] = useAverageForSummary(key)
      ? Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(4))
      : Math.round(nums.reduce((a, b) => a + b, 0))
  })
  return summary
}

/** Build results payload for AI: add summaryStatistics (stats), sampleData, and aggregated description. */
function buildResultsForAI(
  data: unknown,
  resultType: string,
  metadata?: Record<string, unknown>
): Record<string, unknown> {
  const statsColumnTypes = metadata?.statsColumnTypes as Record<string, string> | undefined
  const arr = Array.isArray(data) ? data : []
  const out: Record<string, unknown> = {
    rowCount: arr.length,
    resultType: resultType || 'table',
  }
  if (resultType === 'stats' && arr.length > 0) {
    const rows = arr as Record<string, unknown>[]
    out.summaryStatistics = computeSummaryStatistics(rows, statsColumnTypes)
    out.aggregatedDescription = `Aggregated across all ${rows.length} rows (same as Summary statistics in the UI).`
  }
  if (arr.length > 0) {
    out.sampleData = arr.slice(0, SAMPLE_ROWS)
    if (arr.length > SAMPLE_ROWS) {
      out.sampleNote = `Sample: first ${SAMPLE_ROWS} of ${arr.length} rows. Use summaryStatistics for stats output.`
    }
  }
  out.data = data
  if (Array.isArray(data)) out.count = data.length
  return out
}

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 10 * 60 * 1000 // 10 minutes

export type CallAIFn = (
  prompt: string,
  systemPrompt: string,
  options?: { temperature?: number; maxTokens?: number; signal?: AbortSignal }
) => Promise<string | null>

export interface RunExecuteJobParams {
  scriptId: string
  datasetId: string
  /** Symbol for partitioned datasets */
  symbol?: string
  /** Date filter (YYYY-MM-DD) */
  dateStart?: string
  dateEnd?: string
  /** Limit rows in result (optional) */
  limitRows?: number
}

export interface RunExecuteJobResult {
  jobId: string
  status: 'completed' | 'failed'
  error?: string
  result?: {
    data: unknown
    resultType?: 'table' | 'stats' | 'signals' | 'backtest'
    metadata?: Record<string, unknown>
  }
}

/**
 * Create an execute job and poll until it completes or fails.
 * Returns the job and, if completed, the result payload from GET /jobs/:id/result.
 */
export async function runExecuteJob(params: RunExecuteJobParams): Promise<RunExecuteJobResult> {
  const { scriptId, datasetId, symbol, dateStart, dateEnd, limitRows } = params

  const job = await jobsApi.create({
    jobType: 'execute',
    config: {
      scriptId,
      datasetId,
      options: {
        selectedSymbol: symbol,
        dateFilter:
          dateStart && dateEnd
            ? { startDate: dateStart, endDate: dateEnd }
            : undefined,
        limitRows: limitRows ?? 10000,
        executionMode: 'async',
      },
    },
  })

  const deadline = Date.now() + POLL_TIMEOUT_MS
  let lastJob = job

  while (Date.now() < deadline) {
    if (lastJob.status === 'completed') {
      try {
        const resultId = (lastJob as any).resultId || lastJob.id
        const response = await apiClient.get(`/jobs/${resultId}/result`)
        return {
          jobId: lastJob.id,
          status: 'completed',
          result: response.data,
        }
      } catch (e) {
        return {
          jobId: lastJob.id,
          status: 'failed',
          error: e instanceof Error ? e.message : 'Failed to fetch result',
        }
      }
    }
    if (lastJob.status === 'failed' || lastJob.status === 'cancelled') {
      return {
        jobId: lastJob.id,
        status: 'failed',
        error: (lastJob as any).error ?? lastJob.status,
      }
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
    lastJob = await jobsApi.get(lastJob.id)
  }

  return {
    jobId: lastJob.id,
    status: 'failed',
    error: 'Job timed out',
  }
}

export interface AnalyzeResultWithAIParams {
  result: { data: unknown; resultType?: string; metadata?: Record<string, unknown> }
  scriptDescription?: string
  scriptCode?: string
  datasetInfo?: ResultsExplanationContext['datasetInfo']
  callAI: CallAIFn
}

const ANALYSIS_SYSTEM_PROMPT = `You are an expert quantitative analyst. Analyze the provided script results and market data context. Respond with a clear, structured analysis: executive summary, key metrics, trading insights, and limitations. Use the framework requested in the user prompt.`

/**
 * Build the results explanation prompt and call the AI. Returns the analysis text.
 */
export async function analyzeResultWithAI(params: AnalyzeResultWithAIParams): Promise<string> {
  const { result, scriptDescription, scriptCode, datasetInfo, callAI } = params
  const resultTypeRaw = (result.resultType as string) || 'table'
  const resultType = resultTypeRaw as ResultsExplanationContext['resultType']
  const results =
    typeof result.data === 'object' && result.data !== null
      ? buildResultsForAI(result.data, resultTypeRaw, result.metadata)
      : { data: result.data, rowCount: 0, resultType }

  const context: ResultsExplanationContext = {
    results,
    scriptDescription,
    scriptCode,
    datasetInfo,
    resultType,
  }

  const prompt = generateResultsExplanationPrompt(context)
  const analysis = await callAI(prompt, ANALYSIS_SYSTEM_PROMPT, {
    temperature: 0.3,
    maxTokens: 4000,
  })
  return analysis ?? 'No analysis generated.'
}

export interface RunAndAnalyzeParams {
  scriptId: string
  datasetId: string
  symbol?: string
  dateStart?: string
  dateEnd?: string
  limitRows?: number
  scriptDescription?: string
  scriptCode?: string
  datasetInfo?: ResultsExplanationContext['datasetInfo']
  callAI: CallAIFn
}

export interface RunAndAnalyzeResult {
  jobId: string
  runStatus: 'completed' | 'failed'
  runError?: string
  result?: RunExecuteJobResult['result']
  analysis?: string
  analysisError?: string
}

/**
 * Run the script on the dataset, then analyze the result with the AI.
 * Single entry point for the frontend AI agent "run and analyze" capability.
 */
export async function runAndAnalyze(params: RunAndAnalyzeParams): Promise<RunAndAnalyzeResult> {
  const runResult = await runExecuteJob({
    scriptId: params.scriptId,
    datasetId: params.datasetId,
    symbol: params.symbol,
    dateStart: params.dateStart,
    dateEnd: params.dateEnd,
    limitRows: params.limitRows,
  })

  const out: RunAndAnalyzeResult = {
    jobId: runResult.jobId,
    runStatus: runResult.status,
    runError: runResult.error,
    result: runResult.result,
  }

  if (runResult.status === 'failed' || !runResult.result) {
    return out
  }

  try {
    out.analysis = await analyzeResultWithAI({
      result: runResult.result,
      scriptDescription: params.scriptDescription,
      scriptCode: params.scriptCode,
      datasetInfo: params.datasetInfo,
      callAI: params.callAI,
    })
  } catch (e) {
    out.analysisError = e instanceof Error ? e.message : 'Analysis failed'
  }

  return out
}
